import { useState } from "react";
import { Link } from "react-router-dom";
import { useCustomer } from "@/context/CustomerAuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { Truck, Package, Home, Boxes, ArrowRight, UserCircle } from "lucide-react";
import PackersMoversFlow from "@/flows/PackersMoversFlow";
import PickupDropFlow from "@/flows/PickupDropFlow";
import ThreePLFlow from "@/flows/ThreePLFlow";
import { HomeBanner } from "@/components/HomeBanner";
import { ClientsMarquee } from "@/components/ClientsMarquee";
import { Testimonials } from "@/components/Testimonials";

const SERVICES = [
  { k: "pickup_drop", label: "Pickup & Drop", desc: "Send a parcel — instant volumetric pricing and pay online.", icon: Package },
  { k: "packers", label: "Packers & Movers", desc: "Full home or industrial relocation survey and quotation.", icon: Home },
  { k: "3pl", label: "3PL Services", desc: "E-commerce fulfilment — talk to our experts.", icon: Boxes },
];

export default function PublicForm() {
  const [service, setService] = useState(null);
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      {!service ? (
        <>
          <HomeBanner />
          <ServiceSelect onSelect={setService} />
          <ClientsMarquee />
          <Testimonials />
        </>
      ) : service === "packers" ? (
        <PackersMoversFlow onBack={() => setService(null)} />
      ) : service === "pickup_drop" ? (
        <PickupDropFlow onBack={() => setService(null)} />
      ) : (
        <ThreePLFlow onBack={() => setService(null)} />
      )}
    </div>
  );
}

function ServiceSelect({ onSelect }) {
  return (
    <div id="services" className="max-w-5xl mx-auto w-full px-6 py-12 scroll-mt-20">
      <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Arrow Logistics</div>
      <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">Fast pick-ups. Seamless logistics. On-target delivery.</h1>
      <p className="text-muted-foreground mt-3 max-w-2xl">Choose a service to get started. Each one is tailored to a different kind of shipment.</p>
      <div className="mt-10 grid gap-5 md:grid-cols-3" data-testid="service-select">
        {SERVICES.map((s) => (
          <button key={s.k} type="button" data-testid={`service-${s.k}`} onClick={() => onSelect(s.k)}
            className="group text-left border border-border bg-card p-7 hover:border-primary transition-colors flex flex-col">
            <div className="h-12 w-12 grid place-items-center bg-primary/10 text-primary mb-5 group-hover:bg-primary group-hover:text-primary-foreground transition-colors"><s.icon className="h-6 w-6" /></div>
            <div className="font-heading font-bold text-xl uppercase tracking-tight">{s.label}</div>
            <p className="text-sm text-muted-foreground mt-2 flex-1">{s.desc}</p>
            <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-primary">Select <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Header() {
  const { customer } = useCustomer();
  return (
    <header className="h-16 border-b border-border bg-card sticky top-0 z-20 flex items-center justify-between px-6">
      <div className="flex items-center gap-2"><Logo className="h-9" /></div>
      <div className="flex items-center gap-3">
        <ThemeToggle />
        <Link to={customer ? "/my-account" : "/signin"} className="text-sm font-bold uppercase tracking-wide border border-border px-3 sm:px-4 py-2 hover:border-primary hover:text-primary transition-colors flex items-center gap-2" data-testid={customer ? "my-account-link" : "customer-signin-link"}>
          <UserCircle className="h-4 w-4" /><span className="hidden sm:inline">{customer ? "My Account" : "Sign In"}</span>
        </Link>
        <Link to="/track-shipment" className="text-sm font-bold uppercase tracking-wide bg-primary text-primary-foreground px-4 py-2 hover:opacity-90 transition-opacity flex items-center gap-2" data-testid="track-consignment-link"><Truck className="h-4 w-4" /><span className="hidden sm:inline">Track Consignment</span><span className="sm:hidden">Track</span></Link>
        <Link to="/login" className="text-sm font-bold uppercase tracking-wide border border-border px-4 py-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors hidden sm:inline-block" data-testid="staff-login-link">Staff login</Link>
      </div>
    </header>
  );
}
